import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/legal/LegalLayout';

export default function PractComTermsPage() {
  return (
    <LegalLayout title="Terms of Service" testId="practcom-terms-page">
      <p>
        These Terms of Service ("Terms") are an agreement between you and The Oncall Bot LLC ("PractCom", "we", "us"
        or "our") and govern your use of PractCom, including the website, web application, patient portal, text and
        email messaging, AI assistant (Dr. G) and related services (the "Service"). By creating an account, starting a
        free trial or using the Service, you agree to these Terms and to our <Link to="/practcom/privacy">Privacy Policy</Link>.
        If you are accepting on behalf of a dental practice or organization, you confirm that you have authority to
        bind it, and "you" means that practice or organization.
      </p>

      <h2>1. Who may use PractCom</h2>
      <p>
        PractCom is a business service for licensed dental professionals, dental practices, dental service
        organizations and their authorized staff ("Practices"). Patients use the patient portal and messages only at
        the direction of their Practice. You must be at least 18 years old and able to form a binding contract.
      </p>

      <h2>2. Accounts and security</h2>
      <ul>
        <li>You must give accurate information and keep it up to date.</li>
        <li>You are responsible for everything done under your account and your team members' accounts, and for keeping passwords confidential.</li>
        <li>Give each team member their own login. Tell us right away at admin@practcom.com if you suspect unauthorized access.</li>
      </ul>

      <h2>3. Free trial</h2>
      <p>
        New Practices may use the Service free for 15 days. No credit card is required to start, and nothing is
        charged automatically when the trial ends. To keep using the Service after the trial, you choose a paid plan
        inside the app. If you do not choose a plan, your access is paused. We may change or end trial offers for
        future sign-ups at any time.
      </p>

      <h2>4. Plans, billing and usage allowances</h2>
      <ul>
        <li>Paid plans are billed in advance, monthly or annually, through our payment processor. Prices are shown on our website and in the app and do not include applicable taxes.</li>
        <li>Each plan includes monthly allowances (for example active patients, texts, emails, AI actions and SmileDesign renders). We email you when you reach 80% and 100% of an allowance. Nothing is added to your bill unless you choose to add an add-on pack or change plans.</li>
        <li>Plans renew automatically until cancelled. You can cancel at any time in the app or by emailing us; cancellation takes effect at the end of the current billing period. Fees already paid are not refundable except where required by law.</li>
        <li>We may change prices with at least 30 days' notice. Changes apply from your next renewal.</li>
        <li>If a payment fails, we may suspend the Service after giving you notice and a reasonable chance to update your payment details.</li>
      </ul>

      <h2>5. HIPAA and the Business Associate Agreement</h2>
      <p>
        When you use PractCom to send or store protected health information ("PHI"), we act as your business
        associate under HIPAA. Before any patient text or email can be sent, an authorized representative of your
        Practice must sign our Business Associate Agreement ("BAA") inside the app. A signed copy is kept in Practice
        Settings. If these Terms and the BAA conflict on how PHI is handled, the BAA controls.
      </p>

      <h2>6. Your responsibilities as a Practice</h2>
      <ul>
        <li><strong>Patient consent.</strong> You confirm that you have each patient's permission to contact them by text message and email at the numbers and addresses you enter, as required by the Telephone Consumer Protection Act (TCPA), CAN-SPAM and similar laws.</li>
        <li><strong>Accurate patient data.</strong> You are responsible for the accuracy of patient names, phone numbers, email addresses and other information you upload or import.</li>
        <li><strong>Lawful use.</strong> You will use the Service only in line with HIPAA, state privacy and dental board rules, and other applicable laws.</li>
        <li><strong>Your team.</strong> You are responsible for your staff's use of the Service and for removing access when someone leaves.</li>
      </ul>

      <h2>7. Text messages (SMS)</h2>
      <p>
        PractCom sends text messages on behalf of Practices, such as post-operative instructions, consent requests,
        Med Cards, check-ins and appointment-related messages. Message frequency varies by treatment. Message and
        data rates may apply. A patient can reply <strong>STOP</strong> at any time to stop receiving texts from a
        Practice, and <strong>HELP</strong> for help. Carriers are not liable for delayed or undelivered messages.
        Practices may not use PractCom to send marketing texts to anyone who has not given the required consent.
      </p>

      <h2>8. Clinical content and AI features</h2>
      <ul>
        <li>Post-operative instructions, consents, Med Cards, translations and other content in PractCom are templates for general patient education. They are not a substitute for professional judgment.</li>
        <li>Some features use artificial intelligence, for example Dictate Visit, Dr. G, translations, referral letters and SmileDesign images. AI output can be wrong or incomplete. You must review it before it is sent or relied on. Nothing is sent to a patient from Dictate Visit until you review it and press Send.</li>
        <li>You and your licensed providers remain solely responsible for diagnosis, treatment, prescribing and all clinical decisions, and for any content you send to patients. PractCom does not practice dentistry or medicine.</li>
        <li>PractCom is not an emergency service. Patients with an urgent problem should call their dentist, 911 or go to the nearest emergency room.</li>
      </ul>

      <h2>9. Your data</h2>
      <p>
        You own the patient records, documents and other content you put into the Service ("Your Data"). You give us
        permission to host, process, transmit and display Your Data only as needed to provide and support the Service,
        as described in the BAA and our Privacy Policy. We do not sell Your Data. You can export Your Data while your
        account is active. After your account ends, we keep or delete Your Data as described in the Privacy Policy and
        the BAA.
      </p>

      <h2>10. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>send spam, unlawful, misleading, harassing or infringing messages;</li>
        <li>upload malware or try to break, overload or get around the security of the Service;</li>
        <li>access another Practice's data, or share your login outside your Practice;</li>
        <li>copy, resell or reverse engineer the Service, or use it to build a competing product;</li>
        <li>use the Service in any way that violates law or professional rules.</li>
      </ul>

      <h2>11. Our intellectual property</h2>
      <p>
        The Service, including its software, design, templates, instruction libraries, Med Cards and branding, belongs
        to us and our licensors. While your account is active, we give you a limited, non-exclusive, non-transferable
        right to use the Service and its templates for your Practice. If you send us suggestions or feedback, we may
        use them without obligation to you.
      </p>

      <h2>12. Third-party services</h2>
      <p>
        The Service relies on trusted providers for things like text messaging, email delivery, payments, hosting and
        AI processing. Optional links to outside services (for example Google Business Profile or scheduling tools)
        are governed by those providers' own terms.
      </p>

      <h2>13. Availability and changes</h2>
      <p>
        We work to keep PractCom available and secure, but we do not guarantee it will be uninterrupted or error-free.
        We may update, add or remove features. If we remove a major feature you are paying for, we will tell you in
        advance.
      </p>

      <h2>14. Suspension and termination</h2>
      <p>
        You may stop using the Service and cancel at any time. We may suspend or end your access if you materially
        breach these Terms, fail to pay, or if your use creates a legal or security risk. Where reasonable, we will
        give notice first. Sections that by their nature should continue (such as fees owed, disclaimers, limits on
        liability and indemnification) survive termination.
      </p>

      <h2>15. Disclaimers</h2>
      <p>
        Except as expressly stated in these Terms or the BAA, the Service is provided "as is" and "as available". To the
        fullest extent allowed by law, we disclaim all implied warranties, including merchantability, fitness for a
        particular purpose and non-infringement.
      </p>

      <h2>16. Limitation of liability</h2>
      <p>
        To the fullest extent allowed by law, neither party will be liable for indirect, incidental, special,
        consequential or punitive damages, or for lost profits or revenue. Our total liability arising out of or
        relating to the Service will not exceed the amount you paid us in the 12 months before the event giving rise
        to the claim. These limits do not apply to a party's indemnification obligations or to liability that cannot
        be limited by law.
      </p>

      <h2>17. Indemnification</h2>
      <p>
        You will defend and indemnify us against third-party claims arising from Your Data, your clinical decisions,
        messages you send without required patient consent, or your violation of these Terms or the law.
      </p>

      <h2>18. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the State of New York, without regard to its conflict-of-law rules. Any
        dispute will be brought only in the state or federal courts located in Suffolk County, New York, and both
        parties consent to the jurisdiction of those courts. Before filing a claim, each party agrees to try to resolve
        the dispute informally by contacting the other for at least 30 days.
      </p>

      <h2>19. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. If we make a material change, we will notify you by email or in the
        app at least 15 days before it takes effect. Continuing to use the Service after that date means you accept the
        updated Terms.
      </p>

      <h2>20. General</h2>
      <p>
        These Terms, the BAA, your selected plan and our Privacy Policy are the entire agreement between us about the
        Service. If any part is found unenforceable, the rest stays in effect. You may not assign these Terms without our
        consent; we may assign them in connection with a merger or sale of our business. Failure to enforce a provision
        is not a waiver.
      </p>
    </LegalLayout>
  );
}
